/**
 * One-line "On view" shelf for the events list.
 *
 * Date-ranged runs (exhibitions, installations) are deliberately kept OUT of the
 * day-by-day feed — a six-week show has no single day, so putting it in the list
 * would surface it once and then drop it. Instead the list gets this single
 * collapsed row, and only when something is actually open. Nothing renders when
 * there is nothing on view, so a normal day looks exactly as it did before.
 *
 * The row expands in place to show closing dates, and links to the full lane at
 * `/events/on-view`.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { ExtractedEvent } from '../types';

function closesOn(iso: string | undefined): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const days = Math.max(0, Math.ceil((ms - Date.now()) / 86_400_000));
  if (days === 0) return 'closes today';
  if (days === 1) return 'closes tomorrow';
  if (days <= 7) return `closes in ${days}d`;
  return `through ${new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

export default function OnViewShelf() {
  const [runs, setRuns] = useState<ExtractedEvent[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await api.get<ExtractedEvent[]>('/api/events/on-view');
        if (!cancelled) setRuns(Array.isArray(rows) ? rows : []);
      } catch {
        // Decorative furniture — a failure must never disturb the events list.
        if (!cancelled) setRuns([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (runs.length === 0) return null;

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center gap-2 flex-wrap small">
        <span className="badge text-bg-light border">
          <i className="bi bi-easel" aria-hidden="true" /> On view now
        </span>
        <span className="text-body-secondary">
          {runs.length} exhibition{runs.length === 1 ? '' : 's'}
          {!open && runs[0] ? ` · ${runs[0].title}` : ''}
          {!open && runs.length > 1 ? ` and ${runs.length - 1} more` : ''}
        </span>
        <button
          type="button"
          className="btn btn-link btn-sm p-0"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? 'Hide' : 'Show'}
        </button>
        <Link className="ms-auto" to="/events/on-view">All on view →</Link>
      </div>

      {open && (
        <ul className="list-unstyled small mt-2 mb-0 ps-1">
          {runs.map((r) => (
            <li key={`${r.partitionKey}/${r.rowKey}`} className="mb-1">
              <Link to="/events/on-view">{r.title}</Link>
              {(r.venueName || r.locationText) && (
                <span className="text-body-secondary"> — {r.venueName || r.locationText}</span>
              )}
              {closesOn(r.endUtc) && <span className="text-body-secondary"> ({closesOn(r.endUtc)})</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
